import React, { useEffect, useRef, useState } from 'react';

// Home-page hero background: a looping, muted port/logistics video over its
// own first frame (the poster). Only the poster is shown when:
//  - the visitor has "reduce motion" turned on,
//  - the browser reports data saver or a slow connection (Network
//    Information API — Chrome/Android; Safari doesn't expose it, so there
//    the video plays unless reduce motion is on),
//  - or autoplay is refused (e.g. iOS Low Power Mode).
// The video stays invisible until it's actually playing, so a blocked or
// slow start never shows a play button or a blank frame — just the poster.

// Defaults; Admin > Website content > Home can replace both.
const VIDEO = '/hero.mp4';
const POSTER = '/hero-poster.jpg';
const SLOW = ['slow-2g', '2g', '3g'];
// Played slower than recorded so the montage (and its cuts) feels calm:
// 35 s of footage → ~47 s on screen.
const PLAYBACK_RATE = 0.75;
// Seconds before the end at which the video fades out to the poster. The
// poster is the video's first frame, so the loop restarts from exactly what
// is showing — a soft transition instead of a hard cut back to the start.
const LOOP_FADE = 1.8;

function videoAllowed() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const c = navigator.connection;
  if (c && (c.saveData || SLOW.includes(c.effectiveType))) return false;
  return true;
}

export default function HeroVideo({ video = VIDEO, poster = POSTER }) {
  const [allowed, setAllowed] = useState(false); // decided after mount; poster first
  const [playing, setPlaying] = useState(false);
  const [fading, setFading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const update = () => setAllowed(videoAllowed());
    update();
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', update);
    navigator.connection?.addEventListener?.('change', update);
    return () => {
      mq.removeEventListener('change', update);
      navigator.connection?.removeEventListener?.('change', update);
    };
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!allowed || !v) {
      setPlaying(false);
      return;
    }
    // React doesn't reliably reflect `muted` to the DOM attribute, and iOS
    // only autoplays videos that are muted — so set it directly.
    v.muted = true;
    v.defaultPlaybackRate = PLAYBACK_RATE;
    v.playbackRate = PLAYBACK_RATE;
    v.play().catch(() => setPlaying(false));
  }, [allowed]);

  return (
    <div className="hero-media" aria-hidden="true">
      <img className="hero-poster" src={poster} alt="" decoding="async" fetchpriority="high" />
      {allowed && (
        <video
          key={video}
          ref={ref}
          className={`hero-video${playing ? ' is-playing' : ''}${fading ? ' is-fading' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          disablePictureInPicture
          onPlaying={() => setPlaying(true)}
          // Some browsers reset the rate when the source (re)loads.
          onLoadedMetadata={(e) => { e.currentTarget.playbackRate = PLAYBACK_RATE; }}
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            const nearEnd = v.duration > 0 && v.duration - v.currentTime <= LOOP_FADE * PLAYBACK_RATE;
            if (nearEnd !== fading) setFading(nearEnd);
          }}
        >
          <source src={video} type={video.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
        </video>
      )}
    </div>
  );
}
